import { useEffect, useState } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import { InferGetStaticPropsType } from 'next';
import { Carousel, GiftCardSidebar, ImageGallery, Layout, Modal, ProductView, RecommendationCarousel } from '@components';
import { getGlobalProps } from '@utils/get-global-props';
import { getVariantByProperty } from '@utils/shopify';
import { getProduct } from '@services/shopify';
import { useIsMobile, useTranslation } from '@utils/hooks';
import { LARGE_WIDTH, LOCALE_DICT, PRODUCT_TYPES } from '@constants';
import { NormalizedProduct } from '@ts/product';

const getTranslatedTexts = translator => {
	return {
		otherGifts: translator('other-gifts'),
	};
};

const GiftCardPage = ({ product }: InferGetStaticPropsType<typeof getStaticProps>) => {
	const isMobile = useIsMobile({ maxWidth: LARGE_WIDTH });
	const isDoubleColImage = useFeatureIsOn('is-double-col-image');
	const isSingleColImage = useFeatureIsOn('is-single-col-image');

	const { query } = useRouter();
	const defaultVariant = query.variantId
		? getVariantByProperty(product, 'id', query.variantId as string)
		: product?.variants[0];
	const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
	const [selectedImgIndex, setSelectedImgIndex] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalType, setModalType] = useState<'VTO' | 'Imagery'>('Imagery');
	const { name, variants } = product;
	const { translator } = useTranslation();
	const translations = getTranslatedTexts(translator);

	useEffect(() => {
		setSelectedVariant(defaultVariant);
	}, [defaultVariant]);

	const altText = `${selectedVariant.type} ${selectedVariant.option}`;

	return (
		<Modal open={isModalOpen} onOpenChange={setIsModalOpen}>
			<ProductView.Root
				seo={{
					product: {
						...product,
						images: [{ ...selectedVariant.image, altText: altText }],
						suggestedGender: 'Unisex',
						variants: [selectedVariant],
					},
				}}
			>
				<ProductView.Images>
					{(isSingleColImage || isDoubleColImage) && !isMobile ? (
						<ImageGallery
							images={[{ ...selectedVariant.image, altText: altText }]}
							name={name}
							type={selectedVariant.type}
							imageLayoutType='single'
						/>
					) : (
						<Carousel
							images={[{ ...selectedVariant.image, altText: altText }]}
							isModalOpen={isModalOpen}
							modalType={modalType}
							name={name}
							type={selectedVariant.type}
							selectedImgIndex={selectedImgIndex}
							setSelectedImgIndex={setSelectedImgIndex}
							setModalType={setModalType}
						/>
					)}
				</ProductView.Images>
				<GiftCardSidebar
					selectedVariant={selectedVariant}
					variants={variants}
					name={name}
					setSelectedVariant={setSelectedVariant}
				/>
				<ProductView.Recommendations>
					<RecommendationCarousel
						variant={selectedVariant}
						selectedFrame=''
						headingText={translations.otherGifts}
						productTypes={[PRODUCT_TYPES.ACCESSORY]}
					/>
				</ProductView.Recommendations>
			</ProductView.Root>
		</Modal>
	);
};

export async function getStaticProps({ locale }) {
	try {
		const { countryCode: country, languageCode: language } = LOCALE_DICT[locale];
		const product = (await getProduct('gift-card', {
			skipImages: true,
			country,
			language,
			maxVariants: 75,
		})) as NormalizedProduct;
		if (!product) {
			return {
				notFound: true,
			};
		}

		const globalProps = await getGlobalProps({ locale });
		return {
			props: {
				...globalProps,
				product,
				page: 'product',
			},
		};
	} catch (error) {
		console.error(`SSG error in /gift-card: ${error}`);
		return {
			notFound: true,
			revalidate: 600,
		} as const;
	}
}

GiftCardPage.getLayout = function getLayout(page, pageProps) {
	return <Layout pageProps={pageProps}>{page}</Layout>;
};

export default GiftCardPage;
